import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { BedDouble, Bath, Sofa, MapPin, Check, ArrowLeft, Phone, Mail, Zap, Layers, Film, Heart, CalendarClock, Train, GraduationCap, ShoppingBag, Trees } from "lucide-react";
import { COMPANY } from "../mock";
import { formatPrice } from "../components/PropertyCard";
import { PropertyMap } from "../components/PropertyMap";
import { MediaGallery } from "../components/MediaGallery";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../hooks/use-toast";
import { submitEnquiry, fetchProperty, bookViewing } from "../api";
import Seo from "../components/Seo";
import HMOHelper from "../components/HMOHelper";

const PropertyDetail = () => {
  const { slug } = useParams();
  const { toast } = useToast();
  const [property, setProperty] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [activeImg, setActiveImg] = useState(0);
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "", preferredDate: "" });
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState("enquiry");

  useEffect(() => {
    setActiveImg(0);
    fetchProperty(slug)
      .then((data) => setProperty(data))
      .catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center px-5">
        <h1 className="font-display text-[32px] text-[#2b2b2b] mb-3">Property not found</h1>
        <Link to="/buying" className="text-[#b8935f] inline-flex items-center gap-2"><ArrowLeft size={16} /> Back to listings</Link>
      </div>
    );
  }

  if (!property) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <p className="font-display text-[22px] text-[#8a8a8a]">Loading property...</p>
      </div>
    );
  }

  const isRent = property.status === "to-rent";

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast({ title: "Please complete the form", description: "Name and email are required." });
      return;
    }
    setLoading(true);
    try {
      if (mode === "viewing") {
        await bookViewing({
          name: form.name, email: form.email, phone: form.phone, message: form.message,
          propertyId: property.id, propertyTitle: property.title, preferredDate: form.preferredDate,
        });
        toast({ title: "Viewing requested", description: `We'll confirm your viewing of ${property.title} shortly.` });
      } else {
        await submitEnquiry({ ...form, source: "property", propertyId: property.id, propertyTitle: property.title });
        toast({ title: "Enquiry sent", description: `Thank you — we'll be in touch about ${property.title}.` });
      }
      setForm({ name: "", email: "", phone: "", message: "", preferredDate: "" });
    } catch {
      toast({ title: "Something went wrong", description: "Please try again." });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-[#faf7f2]">
      <Seo
        title={property.title}
        description={(property.summary || `${property.bedrooms} bedroom ${property.type} in ${property.postcode || "Bath"}. ${isRent ? "Available to rent" : "For sale"} with Anadear Estates.`).slice(0, 300)}
        path={`/property/${property.slug}`}
        image={(property.images || [])[0]}
        type="article"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Residence",
          "name": property.title,
          "description": property.summary,
          "url": `https://anadearestates.com/property/${property.slug}`,
          "image": property.images || [],
          "numberOfRooms": property.bedrooms,
          "address": { "@type": "PostalAddress", "addressLocality": "Bath", "postalCode": property.postcode, "addressCountry": "GB" },
          "offers": { "@type": "Offer", "price": property.price, "priceCurrency": "GBP", "availability": "https://schema.org/InStock" }
        }}
      />
      <div className="max-w-[1280px] mx-auto px-5 md:px-8 pt-8">
        <Link to={isRent ? "/lettings" : "/buying"} className="inline-flex items-center gap-2 text-[14px] text-[#6a6a6a] hover:text-[#b8935f] transition-colors mb-6">
          <ArrowLeft size={16} /> Back to {isRent ? "lettings" : "properties for sale"}
        </Link>
      </div>

      {/* Gallery */}
      <div className="max-w-[1280px] mx-auto px-5 md:px-8">
        <MediaGallery images={property.images || []} floorplans={property.floorplans || []} title={property.title} isRent={isRent} />
      </div>

      <div className="max-w-[1280px] mx-auto px-5 md:px-8 py-14 grid lg:grid-cols-[1.6fr_1fr] gap-12">
        <div>
          <p className="text-[12px] tracking-[0.2em] uppercase text-[#b8935f] mb-2">{property.type}</p>
          <h1 className="font-display text-[32px] md:text-[42px] font-300 text-[#2b2b2b] mb-3">{property.title}</h1>
          <p className="flex items-center gap-2 text-[15px] text-[#6a6a6a] mb-6"><MapPin size={17} className="text-[#b8935f]" /> {property.address}, {property.postcode}</p>

          <div className="flex items-center gap-8 pb-8 border-b border-[#e6ddd0] flex-wrap">
            <span className="flex items-center gap-2 text-[15px] text-[#4a4a4a]"><BedDouble size={20} className="text-[#b8935f]" /> {property.bedrooms} Bed</span>
            <span className="flex items-center gap-2 text-[15px] text-[#4a4a4a]"><Bath size={20} className="text-[#b8935f]" /> {property.bathrooms} Bath</span>
            {property.receptions > 0 && <span className="flex items-center gap-2 text-[15px] text-[#4a4a4a]"><Sofa size={20} className="text-[#b8935f]" /> {property.receptions} Reception</span>}
            {property.epc && <span className="flex items-center gap-2 text-[15px] text-[#4a4a4a]"><Zap size={20} className="text-[#b8935f]" /> EPC {property.epc}</span>}
          </div>

          <div className="py-8 border-b border-[#e6ddd0]">
            <h2 className="font-display text-[24px] font-500 text-[#2b2b2b] mb-4">Description</h2>
            <p className="text-[15px] text-[#6a6a6a] leading-relaxed mb-4">{property.summary}</p>
            <p className="text-[15px] text-[#6a6a6a] leading-relaxed">{property.description}</p>
          </div>

          <div className="py-8">
            <h2 className="font-display text-[24px] font-500 text-[#2b2b2b] mb-5">Key Features</h2>
            <ul className="grid sm:grid-cols-2 gap-3">
              {property.features.map((f) => (
                <li key={f} className="flex items-start gap-2.5">
                  <Check size={17} className="text-[#b8935f] mt-0.5 flex-shrink-0" />
                  <span className="text-[14px] text-[#4a4a4a]">{f}</span>
                </li>
              ))}
            </ul>
          </div>

          {property.floorplans && property.floorplans.length > 0 && (
            <div className="py-8 border-t border-[#e6ddd0]">
              <h2 className="font-display text-[24px] font-500 text-[#2b2b2b] mb-5 flex items-center gap-2"><Layers size={20} className="text-[#b8935f]" /> Floorplans</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                {property.floorplans.map((fp, i) => (
                  <img key={i} src={fp} alt={`Floorplan ${i + 1}`} className="rounded-xl border border-[#f0ece5] w-full" />
                ))}
              </div>
            </div>
          )}

          {property.epcDocs && property.epcDocs.length > 0 && (
            <div className="py-8 border-t border-[#e6ddd0]" data-testid="property-epc-docs">
              <h2 className="font-display text-[24px] font-500 text-[#2b2b2b] mb-5 flex items-center gap-2"><Zap size={20} className="text-[#b8935f]" /> Energy Performance Certificate</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                {property.epcDocs.map((d, i) => /\.pdf($|\?)/i.test(d)
                  ? <a key={i} href={d} target="_blank" rel="noreferrer" className="text-[#b8935f] underline text-[15px]">View EPC certificate (PDF)</a>
                  : <img key={i} src={d} alt={`EPC ${i + 1}`} className="rounded-xl border border-[#f0ece5] w-full" />)}
              </div>
            </div>
          )}

          {property.video && (
            <div className="py-8 border-t border-[#e6ddd0]">
              <h2 className="font-display text-[24px] font-500 text-[#2b2b2b] mb-5 flex items-center gap-2"><Film size={20} className="text-[#b8935f]" /> Video Tour</h2>
              <div className="aspect-video rounded-2xl overflow-hidden">
                <iframe title="Video tour" src={property.video} className="w-full h-full" allowFullScreen frameBorder="0" />
              </div>
            </div>
          )}

          <div className="py-8 border-t border-[#e6ddd0]">
            <h2 className="font-display text-[24px] font-500 text-[#2b2b2b] mb-5 flex items-center gap-2"><MapPin size={20} className="text-[#b8935f]" /> Location</h2>
            <PropertyMap lat={property.lat} lng={property.lng} title={property.title} />
            <h3 className="font-display text-[18px] font-500 text-[#2b2b2b] mt-8 mb-4">What's Nearby</h3>
            <div className="grid sm:grid-cols-2 gap-3">
              {[
                { icon: Train, label: "Bath Spa Station", meta: "0.6 miles" },
                { icon: GraduationCap, label: "Outstanding schools", meta: "Within 1 mile" },
                { icon: ShoppingBag, label: "Independent shops & cafés", meta: "5 min walk" },
                { icon: Trees, label: "Royal Victoria Park", meta: "0.8 miles" },
              ].map((n) => (
                <div key={n.label} className="flex items-center gap-3 p-4 rounded-xl bg-[#faf7f2] border border-[#f0ece5]">
                  <n.icon size={20} className="text-[#b8935f]" />
                  <div>
                    <p className="text-[14px] text-[#2b2b2b]">{n.label}</p>
                    <p className="text-[12px] text-[#8a8a8a]">{n.meta}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <HMOHelper property={property} />
        </div>

        {/* Sidebar */}
        <aside>
          <div className="sticky top-[100px] bg-white rounded-2xl border border-[#f0ece5] shadow-[0_12px_40px_rgba(0,0,0,0.06)] p-7">
            <p className="text-[13px] text-[#8a8a8a] mb-1">{property.priceQualifier}</p>
            <p className="font-display text-[34px] font-500 text-[#2b2b2b] mb-6">
              {formatPrice(property)}{isRent && <span className="text-[15px] text-[#8a8a8a] font-400 ml-1">pcm</span>}
            </p>
            <div className="flex gap-2 mb-4 bg-[#faf7f2] p-1.5 rounded-full border border-[#f0ece5]">
              {[["enquiry", "Enquire"], ["viewing", "Book Viewing"]].map(([m, label]) => (
                <button key={m} type="button" onClick={() => setMode(m)} className={`flex-1 py-2 rounded-full text-[13px] transition-colors ${mode === m ? "bg-[#b8935f] text-white" : "text-[#6a6a6a] hover:text-[#2b2b2b]"}`}>{label}</button>
              ))}
            </div>
            <h3 className="font-display text-[19px] font-500 text-[#2b2b2b] mb-4 flex items-center gap-2">
              {mode === "viewing" ? <><CalendarClock size={18} className="text-[#b8935f]" /> Book a viewing</> : "Enquire about this property"}
            </h3>
            <form onSubmit={handleSubmit} className="space-y-3">
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Full name" className="w-full h-[48px] px-4 rounded-lg border border-[#e2d8c8] text-[14px] outline-none focus:ring-2 focus:ring-[#b8935f]/30 focus:border-[#b8935f] transition" />
              <input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email address" className="w-full h-[48px] px-4 rounded-lg border border-[#e2d8c8] text-[14px] outline-none focus:ring-2 focus:ring-[#b8935f]/30 focus:border-[#b8935f] transition" />
              <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Phone number" className="w-full h-[48px] px-4 rounded-lg border border-[#e2d8c8] text-[14px] outline-none focus:ring-2 focus:ring-[#b8935f]/30 focus:border-[#b8935f] transition" />
              {mode === "viewing" && (
                <input type="date" value={form.preferredDate} onChange={(e) => setForm({ ...form, preferredDate: e.target.value })} className="w-full h-[48px] px-4 rounded-lg border border-[#e2d8c8] text-[14px] text-[#4a4a4a] outline-none focus:ring-2 focus:ring-[#b8935f]/30 focus:border-[#b8935f] transition" />
              )}
              <textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder={mode === "viewing" ? "Any preferences for your viewing?" : `I'm interested in ${property.title}...`} rows={3} className="w-full px-4 py-3 rounded-lg border border-[#e2d8c8] text-[14px] outline-none focus:ring-2 focus:ring-[#b8935f]/30 focus:border-[#b8935f] transition resize-none" />
              <button type="submit" disabled={loading} className="w-full h-[50px] rounded-lg bg-[#b8935f] text-white text-[14px] font-500 hover:bg-[#a67f4d] transition-colors disabled:opacity-60">
                {loading ? "Sending..." : mode === "viewing" ? "Request Viewing" : "Send Enquiry"}
              </button>
            </form>
            <div className="mt-6 pt-6 border-t border-[#f0ece5] space-y-3">
              <a href={COMPANY.phoneHref} className="flex items-center gap-3 text-[14px] text-[#4a4a4a] hover:text-[#b8935f] transition-colors"><Phone size={16} className="text-[#b8935f]" /> {COMPANY.phone}</a>
              <a href={`mailto:${COMPANY.email}`} className="flex items-center gap-3 text-[14px] text-[#4a4a4a] hover:text-[#b8935f] transition-colors"><Mail size={16} className="text-[#b8935f]" /> {COMPANY.email}</a>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
};

export default PropertyDetail;
